import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Chip,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
  useMediaQuery,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import {
  useGetTriviaEnginePresetQuery,
  usePreviewTriviaEngineMutation,
  useSaveTriviaEnginePresetMutation,
} from "../../../store/api/mlbApi";
import { resolveCustomCategory } from "../../../utils/triviaArcade";
import TriviaEnginePanel from "./TriviaEnginePanel";
import TriviaInspector from "./TriviaInspector";
import TriviaPackControls from "./TriviaPackControls";
import TriviaPresetRail from "./TriviaPresetRail";
import TriviaPreviewPane from "./TriviaPreviewPane";
import {
  DEFAULT_PACK_OPTIONS,
  buildPreviewRequest,
  engineErrorMessage,
} from "./triviaEnginePack";

const SPORTS = [
  { value: "mlb", label: "MLB" },
  { value: "nba", label: "NBA" },
  { value: "nfl", label: "NFL" },
];

const PANEL_TITLES = {
  preset: "Preset",
  preview: "Engine preview",
  inspector: "Inspector",
};

const clonePreset = (preset) =>
  preset ? JSON.parse(JSON.stringify(preset)) : null;

const TriviaEngineWorkspace = () => {
  const theme = useTheme();
  const isStacked = useMediaQuery(theme.breakpoints.down("md"));

  const [sport, setSport] = useState("mlb");
  const [packOptions, setPackOptions] = useState(DEFAULT_PACK_OPTIONS);
  const [activePreset, setActivePreset] = useState(null);
  const [draftPreset, setDraftPreset] = useState(null);
  const [previewPack, setPreviewPack] = useState(null);
  const [selectedQuestionIndex, setSelectedQuestionIndex] = useState(null);
  const [previewError, setPreviewError] = useState(null);
  const [saveError, setSaveError] = useState(null);

  // Which (sport, version) the draft was seeded from, so the refetch a save
  // triggers does not reset the draft or throw away the operator's pack.
  const loadedRef = useRef({ sport: null, version: null });
  const previewOnLoadRef = useRef(false);

  const {
    data: presetData,
    isLoading: presetLoading,
    isFetching: presetFetching,
    error: presetQueryError,
  } = useGetTriviaEnginePresetQuery(sport);
  const [previewTriviaEngine, previewState] = usePreviewTriviaEngineMutation();
  const [saveTriviaEnginePreset, saveState] = useSaveTriviaEnginePresetMutation();

  // While the preset for a newly selected sport is in flight the draft still
  // belongs to the previous sport, so previewing would use the wrong preset.
  const presetSyncing = presetLoading || presetFetching;
  const questions = previewPack?.questions || [];
  const selectedQuestion =
    selectedQuestionIndex == null
      ? null
      : questions[selectedQuestionIndex] || null;
  const dirty = useMemo(
    () => JSON.stringify(draftPreset) !== JSON.stringify(activePreset),
    [activePreset, draftPreset]
  );

  const runPreview = useCallback(
    async (preset, options, targetSport) => {
      if (!preset) return;
      setPreviewError(null);
      try {
        const response = await previewTriviaEngine(
          buildPreviewRequest({ sport: targetSport, options, preset })
        ).unwrap();
        setPreviewPack(response);
        setSelectedQuestionIndex(response?.questions?.length ? 0 : null);
      } catch (error) {
        setPreviewPack(null);
        setSelectedQuestionIndex(null);
        setPreviewError(engineErrorMessage(error, "Preview failed"));
      }
    },
    [previewTriviaEngine]
  );

  useEffect(() => {
    const preset = presetData?.preset;
    if (!preset || preset.sport !== sport) return;
    if (
      loadedRef.current.sport === sport &&
      loadedRef.current.version === preset.version
    ) {
      return;
    }

    loadedRef.current = { sport, version: preset.version };
    const nextPreset = clonePreset(preset);
    setActivePreset(nextPreset);
    setDraftPreset(clonePreset(nextPreset));
    setPreviewPack(null);
    setSelectedQuestionIndex(null);
    setPreviewError(null);
    setSaveError(null);

    if (previewOnLoadRef.current) {
      previewOnLoadRef.current = false;
      runPreview(nextPreset, packOptions, sport);
    }
  }, [presetData, sport, packOptions, runPreview]);

  const handleSportChange = (_event, nextSport) => {
    if (!nextSport || nextSport === sport) return;
    previewOnLoadRef.current = true;
    setPackOptions((previous) => ({
      ...previous,
      category: resolveCustomCategory(nextSport, previous.category),
    }));
    setSport(nextSport);
  };

  const handleSave = async () => {
    if (!draftPreset || !dirty) return;
    setSaveError(null);
    try {
      const response = await saveTriviaEnginePreset({
        sport,
        fame: draftPreset.fame,
        higherLower: draftPreset.higherLower,
        trueFalse: draftPreset.trueFalse,
        formatMix: draftPreset.formatMix,
      }).unwrap();
      const savedPreset = clonePreset(
        response?.preset || { ...draftPreset, sport }
      );
      loadedRef.current = { sport, version: savedPreset.version };
      setActivePreset(savedPreset);
      setDraftPreset(clonePreset(savedPreset));
    } catch (error) {
      setSaveError(engineErrorMessage(error, "Save failed"));
    }
  };

  const sportLabel =
    SPORTS.find((item) => item.value === sport)?.label || sport.toUpperCase();
  const presetLabel = activePreset?.version
    ? `Active preset v${activePreset.version} · ${sportLabel}`
    : `No active preset · ${sportLabel}`;

  const presetRail = (
    <TriviaPresetRail
      draft={draftPreset}
      active={activePreset}
      dirty={dirty}
      onDraftChange={setDraftPreset}
      onSave={handleSave}
      isSaving={saveState.isLoading}
      error={saveError || engineErrorMessage(presetQueryError)}
    />
  );

  const previewPane = (
    <TriviaPreviewPane
      sport={sport}
      questions={questions}
      selectedQuestion={selectedQuestion}
      selectedIndex={selectedQuestionIndex}
      onSelectQuestion={setSelectedQuestionIndex}
      isLoading={presetSyncing || previewState.isLoading}
      error={previewError || engineErrorMessage(previewState.error)}
      isEmptyPack={Boolean(previewPack) && questions.length === 0}
      presetVersion={previewPack?.presetVersion}
      resampleCount={previewPack?.resampleCount}
    />
  );

  const inspector = <TriviaInspector question={selectedQuestion} />;

  return (
    <Box sx={{ bgcolor: "background.default" }}>
      <Stack
        direction={{ xs: "column", md: "row" }}
        justifyContent="space-between"
        alignItems={{ xs: "flex-start", md: "center" }}
        spacing={2}
        sx={{ mb: 2 }}
      >
        <Box>
          <Typography variant="h4" fontWeight={900}>
            Trivia engine simulator
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Tune the active preset, preview a pack, and inspect why each question
            was picked.
          </Typography>
        </Box>
        <Stack
          direction="row"
          spacing={1.5}
          alignItems="center"
          sx={{ flexWrap: "wrap", rowGap: 1 }}
        >
          <ToggleButtonGroup
            value={sport}
            exclusive
            onChange={handleSportChange}
            aria-label="Trivia sport"
            size="small"
          >
            {SPORTS.map((item) => (
              <ToggleButton key={item.value} value={item.value}>
                {item.label}
              </ToggleButton>
            ))}
          </ToggleButtonGroup>
          <Typography variant="body2" fontWeight={700} color="text.secondary">
            {presetLabel}
          </Typography>
          {dirty && <Chip label="Unsaved" size="small" color="warning" />}
        </Stack>
      </Stack>

      <Box sx={{ mb: 2 }}>
        <TriviaPackControls
          sport={sport}
          options={packOptions}
          onOptionsChange={setPackOptions}
          onRegenerate={() => runPreview(draftPreset, packOptions, sport)}
          isBusy={presetSyncing || previewState.isLoading}
          disabled={!draftPreset}
        />
      </Box>

      {isStacked ? (
        <Stack spacing={2}>
          <TriviaEnginePanel title={PANEL_TITLES.preview}>
            {previewPane}
          </TriviaEnginePanel>
          <TriviaEnginePanel
            title={PANEL_TITLES.inspector}
            collapsible
            defaultExpanded
          >
            {inspector}
          </TriviaEnginePanel>
          <TriviaEnginePanel title={PANEL_TITLES.preset} collapsible>
            {presetRail}
          </TriviaEnginePanel>
        </Stack>
      ) : (
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "280px minmax(0, 1fr) 320px",
            gap: 2,
            alignItems: "start",
          }}
        >
          <TriviaEnginePanel title={PANEL_TITLES.preset}>
            {presetRail}
          </TriviaEnginePanel>
          <TriviaEnginePanel title={PANEL_TITLES.preview}>
            {previewPane}
          </TriviaEnginePanel>
          <TriviaEnginePanel title={PANEL_TITLES.inspector}>
            {inspector}
          </TriviaEnginePanel>
        </Box>
      )}
    </Box>
  );
};

export default TriviaEngineWorkspace;
